import React, { useContext, useMemo } from "react";
import { Badge, Box, IconButton, Popover, Stack, Tooltip, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { AssignedToMeFilter } from "./AssignedToMeFilter";
import { DisputedFilter } from "./DisputedFilter";
import { DisputedReasonFilter } from "./DisputedReasonFilter";
import { DueDateFilter } from "./DueDateFilter";
import { CurrencyFilter } from "./CurrencyFilter";
import { useSuggestedChasableFilters } from "./SuggestedChasableFiltersContext";
import { useCustomersChasablesApi } from "../../../../hooks/useCustomersChasablesApi";
import { ChasablesContext } from "../../../../context/ChasablesContext";

export const SuggestedChasableFilters = () => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const { chasables: apiChasables } = useCustomersChasablesApi();
  const { assignedToUserId } = useContext(ChasablesContext);
  const {
    disputedFilter,
    onDisputedFilterChange,
    disputedReasonFilter,
    onDisputedReasonFilterChange,
    dueDateFilter,
    onDueDateFilterChange,
    currencyFilter,
    onCurrencyFilterChange,
  } = useSuggestedChasableFilters();

  const currencyCount = useMemo(() => {
    const set = new Set<string>();
    for (const c of apiChasables) {
      for (const inv of c.invoices) {
        if (inv.currency) set.add(inv.currency);
      }
    }
    return set.size;
  }, [apiChasables]);
  const showCurrencyFilter = currencyCount > 1;

  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (assignedToUserId !== null) count += 1;
    if (disputedFilter !== "all") count += 1;
    if (disputedReasonFilter.length > 0) count += 1;
    if (dueDateFilter !== "all") count += 1;
    if (showCurrencyFilter && currencyFilter.length > 0) count += 1;
    return count;
  }, [
    assignedToUserId,
    disputedFilter,
    disputedReasonFilter.length,
    dueDateFilter,
    showCurrencyFilter,
    currencyFilter.length,
  ]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "suggested-chasable-filters" : undefined;

  return (
    <>
      <Tooltip title="Filters">
        <Badge
          color="primary"
          badgeContent={
            <Typography variant="body2" color="common.white" lineHeight={1}>
              {appliedFiltersCount}
            </Typography>
          }
          overlap="circular"
          invisible={appliedFiltersCount === 0}
        >
          <IconButton
            aria-describedby={id}
            size="small"
            onClick={handleOpen}
            sx={{
              borderRadius: 1.5,
            }}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
        </Badge>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 8,
          sx: {
            width: 280,
            maxWidth: "100%",
            mt: 1,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Assigned to</Typography>
            <AssignedToMeFilter />
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Filters</Typography>
            <DisputedFilter disputedFilter={disputedFilter} onDisputedFilterChange={onDisputedFilterChange} />
            <DisputedReasonFilter
              disputedReasonFilter={disputedReasonFilter}
              onDisputedReasonFilterChange={onDisputedReasonFilterChange}
            />
            <DueDateFilter dueDateFilter={dueDateFilter} onDueDateFilterChange={onDueDateFilterChange} />
            {showCurrencyFilter && (
              <CurrencyFilter currencyFilter={currencyFilter} onCurrencyFilterChange={onCurrencyFilterChange} />
            )}
          </Stack>
        </Box>
      </Popover>
    </>
  );
};
